import '../index.scss';

import clsx from 'clsx';
import { isEqual, pick } from 'lodash-es';
import {
  forwardRef,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { PanelInstanceContext } from './PanelInstanceContext';
import { PanelsContext } from './PanelsContext';
import { PopinWindow } from './PopinWIndow';
import { PopoutWindow } from './PopoutWindow';
import { PanelPosition, PanelProps, PanelSize } from './types';
import { useRestoreStateFrom } from './usePanelStateHandler';
import { useInterval } from '../common/useInterval';

const defaultInitialSize = { width: 320, height: 200 };

export const Panel = (props: PanelProps): ReactElement => {
  const panelRef = useRef<HTMLDivElement>(null);
  const { title, size = defaultInitialSize, className, dynamicBounds, children } = props;

  const { dockElements, nextStackPosition } = useContext(PanelsContext);
  const { panelPoppedOut, uniqueId, setTitle, dockId, docked, hidden } = useContext(PanelInstanceContext);

  const savedState = useRestoreStateFrom({ uniqueId, panelPoppedOut: false });

  const lastPanelHeightRef = useRef<number>(400);
  // oxlint-disable-next-line react/refs
  lastPanelHeightRef.current = panelRef.current?.clientHeight ?? lastPanelHeightRef.current;

  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  // We only want to update the title when it changes
  useEffect(() => {
    if (panelRef.current) {
      const doc = panelRef.current.ownerDocument;
      if (doc) {
        doc.title = title;
      }
    }
  }, [title]);

  // set inert imperatively: React 18 drops the boolean prop and React 19 rejects
  // the string form, and this library supports both.
  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.inert = hidden ?? false;
    }
  }, [hidden, panelPoppedOut]);

  const [panelPosition, setPanelPosition] = useState((): PanelPosition => {
    if (dynamicBounds) {
      const result = dynamicBounds();
      if (result) {
        return pick(result, ['x', 'y']);
      }
    }

    const pos = props.position;
    if (props.resizeable !== false && savedState?.panelPosition) {
      return savedState.panelPosition;
    }

    if (pos === 'center') {
      const height = size.height === 'auto' ? 400 : size.height;
      return {
        x: Math.max((window.innerWidth - size.width) / 2, 0),
        y: Math.max((window.innerHeight - height) / 2, 0),
      };
    }

    if (pos != null && pos !== 'tile') {
      return pos;
    }

    return nextStackPosition();
  });

  const cropSizeToWindow = useCallback((size: PanelSize): PanelSize => {
    return typeof window !== 'undefined' && window.innerWidth && window.innerHeight
      ? {
          width: Math.min(window.document.body?.clientWidth ?? window.innerWidth, size.width),
          height:
            size.height === 'auto'
              ? 'auto'
              : Math.min(window.document.body?.clientHeight ?? window.innerHeight, size.height),
        }
      : size;
  }, []);

  const [panelSize, _setPanelSize] = useState<PanelSize>(() => {
    const result = dynamicBounds?.();
    if (result) {
      return pick(result, ['width', 'height']);
    }

    if (props.resizeable !== false && savedState?.panelSize) {
      return cropSizeToWindow(savedState.panelSize);
    }

    return size ?? { width: 320, height: 200 };
  });

  const setPanelSize = useCallback(
    (size: PanelSize) => {
      _setPanelSize(cropSizeToWindow(size));
    },
    [cropSizeToWindow],
  );

  const setInitialPanelSize = useCallback(
    (size: PanelSize) => {
      // If panel was already sized from state, then don't resize
      if (props.resizeable !== false && savedState?.panelSize) {
        return;
      }

      setPanelSize(size);
    },
    [props.resizeable, savedState?.panelSize, setPanelSize],
  );

  const dynamicResize = useCallback(() => {
    const bounds = dynamicBounds?.();
    if (!bounds) {
      return;
    }
    const newPosition = pick(bounds, ['x', 'y']);
    if (!isEqual(newPosition, panelPosition)) {
      setPanelPosition(newPosition);
    }
    const newSize = pick(bounds, ['width', 'height']);
    if (!isEqual(newSize, panelSize)) {
      setPanelSize(newSize);
    }
  }, [dynamicBounds, panelPosition, panelSize, setPanelSize]);

  useEffect(() => {
    window.addEventListener('resize', dynamicResize);

    return () => {
      window.removeEventListener('resize', dynamicResize);
    };
  }, [dynamicResize]);

  useInterval(dynamicResize, dynamicBounds ? 250 : null);

  const dockElement = docked && dockId && dockElements[dockId];
  return (
    <>
      {dockElement && dockElement.isConnected ? (
        createPortal(children, dockElement)
      ) : panelPoppedOut ? (
        <PopoutWindow
          name={uniqueId}
          title={title}
          // oxlint-disable-next-line react/refs
          size={{ width: size.width, height: lastPanelHeightRef.current }}
          popInPanelPosition={panelPosition}
          popInPanelSize={{
            width: panelSize.width,
            // oxlint-disable-next-line react/refs
            height: panelSize.height === 'auto' ? lastPanelHeightRef.current : panelSize.height,
          }}
        >
          {hidden && <div className={'PopoutWindowContainer--hidden'} />}
          <div
            ref={panelRef}
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
            }}
            className={className}
          >
            {children}
          </div>
        </PopoutWindow>
      ) : (
        <PopinWindow
          {...props}
          panelPosition={panelPosition}
          setPanelPosition={setPanelPosition}
          panelSize={panelSize}
          setPanelSize={setPanelSize}
          setInitialPanelSize={setInitialPanelSize}
          hidden={hidden}
        >
          {children}
        </PopinWindow>
      )}
    </>
  );
};

export interface PanelContentProps {
  className?: string | undefined;
}

export const PanelContent = forwardRef<HTMLDivElement, PropsWithChildren<PanelContentProps>>(function PanelContentFr(
  { children, className },
  ref,
) {
  return (
    <div className={clsx(`WindowPanel-content`, className)} ref={ref}>
      {children}
    </div>
  );
});
